import React, {useMemo} from 'react';
import {NavLink} from 'react-router-dom';
import {PAGES_URL} from '../../../helpers/constants';
import {connect} from 'react-redux';
import * as Actions from '../../../store/actions';
import {DashboardLayout, PrimaryButton} from 'components';
import {getLighterShade} from 'routes/EasyOnboard/colorUtils';
import UsageProgressBar from '../components/UsageProgressBar';
import {defaultCard, masterCard, visaCard} from '../../../assets/images/images';
import {useGatedQuery} from '../../FraudDetection/Flows/hooks/useGatedQuery';
import {
  fetchSubscription,
  fetchSubscriptionUsage,
} from '../../../requests/queries/flows';
import {calculateSubscriptionStatus} from '../helpers';
import moment from 'moment';

const ROUTES = [
  {
    name: 'Transactions',
    path: PAGES_URL.PROFILED_RISK_TRANSACTIONS,
  },
  {
    name: 'Events Usage',
    path: PAGES_URL.PROFILED_RISK_EVENTS,
    isDisabled: true,
  },
];

function BillingsLayout(props) {
  const {
    children,
    auth: {userDetails},
  } = props;
  const baseColor = userDetails?.company?.color_code || '#3f7cdb';
  const lighterShade = getLighterShade(baseColor, 93);

  const {data, isLoading} = useGatedQuery(
    'company-billing-info',
    fetchSubscription,
  );
  const {data: usageResult, isLoading: usageLoading} = useGatedQuery(
    'company-billing-info-usage',
    fetchSubscriptionUsage,
  );

  const subscriptionInfo = useMemo(() => !isLoading && data, [data, isLoading]);
  const usageInfo = useMemo(
    () => !usageLoading && usageResult?.usage,
    [usageLoading, usageResult],
  );
  const dateInfo = useMemo(
    () => calculateSubscriptionStatus(subscriptionInfo?.plan_expiry_date),
    [subscriptionInfo?.plan_expiry_date],
  );

  const getPaymentBrandLogo = (brand = '') => {
    switch (brand?.toUpperCase()) {
      case 'VISA':
        return visaCard;
      case 'MASTER-CARD':
        return masterCard;
      default:
        return defaultCard;
    }
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Billing</p>
      }
    >
      <div className="flex items-center gap-3 justify-end py-5">
        {/*<SecondaryButton buttonText="Filter" />*/}

        {/*<PrimaryButton buttonText="Add new card" />*/}
      </div>

      <div
        style={{
          backgroundColor: lighterShade,
          border: '1px solid #D9E5F8',
        }}
        className="py-4 px-6 border flex items-center justify-between flex-wrap gap-3 rounded-xl w-full"
      >
        <div className="flex flex-col  gap-1.5">
          <h4 className="text-[#858DAA] text-tiny">Current Tier</h4>
          <p className="text-deepBlue text-semibold text-base">
            {subscriptionInfo?.plan || 'Free Tier'}
          </p>
          <p className="text-small" style={{color: baseColor}}>
            To use premium and save up to 25% on per-event cost{' '}
            <NavLink to={PAGES_URL.PROFILED_RISK_UPGRADE}>
              <span className="font-medium underline underline-offset-1 cursor-pointer">
                Upgrade
              </span>
            </NavLink>
          </p>
        </div>
        <NavLink to={PAGES_URL.PROFILED_RISK_UPGRADE}>
          <PrimaryButton buttonText="Upgrade" />
        </NavLink>
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-xl py-4 px-6 mt-[30px] mb-6">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-[#667185] font-medium">
            Monthly Events Used
          </p>
          <p className="text-sm text-[#1D1D1D] font-medium">
            {dateInfo?.daysRemaining || 'N/A'} days remaining
          </p>
        </div>

        <div className="mb-4">
          <p className="text-lg font-semibold text-[#1D2739]">
            {usageInfo?.used?.toLocaleString() || 0}
            <span className="text-[#959595]">
              {' '}
              / {usageInfo?.limit?.toLocaleString() || 0}
            </span>
          </p>
        </div>

        <UsageProgressBar
          used={usageInfo?.used || 0}
          total={usageInfo?.limit || 0}
          baseColor={baseColor}
        />

        <p className="text-small font-semibold" style={{color: baseColor}}>
          Your subscription is due on{' '}
          {(!isLoading &&
            subscriptionInfo?.plan_expiry_date &&
            moment(subscriptionInfo?.plan_expiry_date).format('MMM D, YYYY')) ||
            'N/A'}
        </p>
      </div>

      <div className="flex mt-8">
        <p className="text-sm text-grey font-medium">Payment Type</p>
      </div>

      <div className="flex items-center justify-between bg-white border border-[#E4E7EC] rounded-xl py-6 px-6 mt-[10px] mb-6">
        <div className="flex items-center gap-2">
          <div>
            <img
              src={getPaymentBrandLogo(subscriptionInfo?.authorization?.brand)}
              alt="card"
              width={34}
            />
          </div>
          <div className="">
            <p className="text-sm text-[#667185] capitalize">
              {subscriptionInfo?.authorization?.card_type || 'N/A'} card{' '}
              <span className="text-[#D9D9D9]">• </span> Expires:{' '}
              {subscriptionInfo?.authorization?.exp_month || '0'}/
              {subscriptionInfo?.authorization?.exp_year || '0'}{' '}
              <span className="text-grey text-xs bottom-[2px] relative">
                {' '}
                - Default{' '}
              </span>
            </p>
            <p className="text-[#3B4454] font-semibold tracking-wider">
              ****** ****** {subscriptionInfo?.authorization?.last4 || '0000'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap ">
          {/* <Button className='!text-xs'>Edit card</Button> */}
          <button
            onClick={() => {}}
            className="bg-[#FCF6F6] text-[#963134] hover:bg-[#963134] transition-all ease-in-out hover:text-white lg:mt-0 w-fit text-xs px-3 h-8 rounded-full border border-[#F4DCDD]"
          >
            Remove card
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="rounded-md bg-[#F7F7F7] w-fit p-1 flex items-center gap-2 overflow-x-auto">
          {ROUTES.map((route, index) => (
            <NavLink
              key={index}
              end
              to={route.path}
              className={({isActive}) =>
                `px-7 py-1.5 min-h-8 text-sm flex items-center justify-center rounded-lg shadow-[0px_4px_32px_-4px,rgba(199,196,196,0.18)] ${
                  isActive
                    ? 'text-black bg-white shadow-tabs'
                    : route.isDisabled
                    ? 'cursor-not-allowed'
                    : 'text-grey-500 bg-transparent'
                }`
              }
              onClick={e => {
                if (route.isDisabled) {
                  e.preventDefault();
                }
              }}
            >
              {route.name}
            </NavLink>
          ))}
        </div>
      </div>
      {children}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(BillingsLayout);
